/**
 * CE-39 R4 — Right-of-title chip cluster for the Reports page header.
 *
 * Composes the chips that sit on the title row beside the page title:
 *  - SandboxBadge (CR-341, when the owning Survey Project is in sandbox)
 *  - AnonymousSurveyChip (when the survey is anonymous)
 *  - A subtle vertical divider, rendered only when BOTH groups are present
 *  - ScopeBadges (when the active role has Course or Faculty Attribute scope)
 *
 * Returns null if neither group renders — keeps the title row layout
 * identical to today for unscoped, non-anonymous reports.
 */
import React from 'react'
import { Box, Divider } from '@mui/material'
import { grey } from '../../../colors'
import { AnonymousSurveyChip } from './AnonymousSurveyChip'
import ScopeBadges from './ScopeBadges'
import SandboxBadge from '../../../components/SandboxBadge'
import { useCurrentUserAttributeScope } from '../hooks/useCurrentUserAttributeScope'

interface ReportHeaderChipsProps {
  isAnonymousSurvey: boolean
  /**
   * Sandbox mode on the owning Survey Project (CR-341). Absent → treated as false.
   *
   * A sandboxed term produces a real report now, so a reader can be looking at one without any
   * other sign of it — the numbers are ordinary numbers. This badge is what distinguishes a
   * rehearsal's report from a real one.
   */
  isSandbox?: boolean
}

const ReportHeaderChips: React.FC<ReportHeaderChipsProps> = ({ isAnonymousSurvey, isSandbox }) => {
  const { appliedAttributeScope } = useCurrentUserAttributeScope()
  const hasScope =
    appliedAttributeScope.courseAttributes.length > 0 ||
    appliedAttributeScope.facultyAttributes.length > 0

  if (!isAnonymousSurvey && !hasScope && isSandbox !== true) return null

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <SandboxBadge isSandbox={isSandbox} />
      {isAnonymousSurvey && <AnonymousSurveyChip show />}
      {isAnonymousSurvey && hasScope && (
        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 0.5, height: 20, alignSelf: 'center', borderColor: grey[300] }}
        />
      )}
      {hasScope && <ScopeBadges appliedAttributeScope={appliedAttributeScope} />}
    </Box>
  )
}

export default ReportHeaderChips
